import { GetExecutionRuleDetails, ExportExecutionRuleDetails } from '../../api/executions-endpoint';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { RuleDetails } from '../../types/execution';
import Container from '@mui/material/Container';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Grid from '@mui/material/Grid';
import useConfigStyles from '../../components/useConfigStyles';
import { Link } from 'react-router-dom';
import { EXECUTIONS, getLinkToExecutionSummaryPage } from '../../navigator/links';
import { defaultRuleDetails } from '../../types/defaultValues';
import AcceptButton from '../../components/AcceptButton';

function ExecutionRuleFailuresPage(): JSX.Element {
  const { jobName } = useParams<{ jobName: string }>();
  const { logId } = useParams<{ logId: string }>();
  const { ruleName } = useParams<{ ruleName: string }>();
  const classes = useConfigStyles();
  const [ruleDetails, setRuleDetails] = useState<RuleDetails>(defaultRuleDetails);
  const [dataColumns, setDataColumns] = useState<string[]>([]);
  const [fetched, setFetched] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');

  const fetchExecutionRuleDetails = useCallback(async () => {
    const response = await GetExecutionRuleDetails(jobName, logId, ruleName);
    if (response.success && response.data) {
      setRuleDetails(response.data);
      if (response.data.Rows.length > 0) {
        setDataColumns(response.data.Rows.sort((b, a) => a.length - b.length)[0].flatMap((value) => value.Column));
      }
    } else {
      setResponseMessage(response.message || '');
    }
    setFetched(response.success);
  }, [jobName, logId, ruleName]);

  useEffect(() => {
    fetchExecutionRuleDetails();
  }, [fetchExecutionRuleDetails]);

  const DataTableHeader = () => {
    return (
      <TableHead>
        <TableRow className={classes.tableHeader}>
          {dataColumns.map((column, ix) => (
            <TableCell key={ix} component='th' scope='row'>
              {column}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  };

  const DataTableBody = () => {
    return (
      <TableBody>
        {Object.entries(ruleDetails.Rows).map(([ix, row]) => (
          <TableRow key={ix}>
            {row.map((rowColumn) => (
              <TableCell key={rowColumn.Column}>{rowColumn.Value || 'N/A'}</TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    );
  };

  const handleExportClick = async () => {
    const response = await ExportExecutionRuleDetails(jobName, logId, ruleName);
    if (response.success && response.data) {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${ruleName}.csv`);
      document.body.appendChild(link);
      link.click();
    }
  };

  const exportButton = () => {
    return ruleDetails.TotalErrors > 0 ? <AcceptButton className={classes.button} label={'Export to CSV'} onClick={handleExportClick} /> : null;
  };

  return !fetched ? (
    <span>{responseMessage}</span>
  ) : (
    <Container>        
      <Breadcrumbs className={classes.breadcrumbs}>
        <Link to={EXECUTIONS}>Executions</Link>
        <Link to={getLinkToExecutionSummaryPage(jobName!, logId!)}>{jobName}</Link>
        <Typography color='textPrimary'>{ruleDetails.Rule}</Typography>
      </Breadcrumbs>
      <Grid container direction='column' justifyContent='center' alignItems='stretch' spacing={'1vw'}>


        <h1>Task List</h1>

        <TableContainer component={Paper}>
          <Table className={classes.table} aria-label='simple table'>
            <TableHead>
              <TableRow className={classes.tableHeader}>
                <TableCell component='th' scope='row'>
                  Type
                </TableCell>
                <TableCell component='th' scope='row'>
                  Rule
                </TableCell>
                <TableCell component='th' scope='row'>
                  Description
                </TableCell>
                <TableCell component='th' scope='row'>
                  Records Found
                </TableCell>
                <TableCell component='th' scope='row'>
                  Records Displayed
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>{ruleDetails.Severity}</TableCell>
                <TableCell>{ruleDetails.Rule}</TableCell>
                <TableCell>{ruleDetails.LongDescription}</TableCell>
                <TableCell>{ruleDetails.TotalErrors}</TableCell>
                <TableCell>{ruleDetails.DisplayedErrors}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <br />

        <TableContainer component={Paper}>
          <Table className={classes.table} stickyHeader aria-label='simple table'>
            <DataTableHeader />
            <DataTableBody />
          </Table>
        </TableContainer>
        <Grid item style={{'marginBottom': '36px'}}>{exportButton()}</Grid>
      </Grid>
    </Container>
  );
}

export default ExecutionRuleFailuresPage;
